import type { ChangeEvent, FocusEvent, KeyboardEvent } from 'react';
import React, { useEffect, useMemo, useState } from 'react';
import { Box } from '../index';
import { skewForDatePicker } from '../DatePicker/DatePicker';
import moment from 'moment';
import { normalizeTimezone } from '../../../lib/moment-timezone-utils';

const padTime = (time: string | number) => `${time}`.padStart(2, '0');

const TimeInput = ({
  value,
  disabled = false,
  onChange,
  onKeyPress,
  timeZone = 'UTC',
}: {
  value?: Date;
  disabled?: boolean;
  onChange?: (date: Date | undefined) => void;
  onKeyPress?: (event: KeyboardEvent<HTMLInputElement>) => void;
  timeZone?: string;
}): JSX.Element => {
  const hourInputRef = React.createRef<HTMLInputElement>();
  const minuteInputRef = React.createRef<HTMLInputElement>();

  const skewedValue = useMemo(
    () => (value ? skewForDatePicker(value, timeZone) : null),
    [value, timeZone],
  );

  const [hours, setHours] = useState<string | undefined>(
    skewedValue ? padTime(skewedValue.getHours()) : undefined,
  );

  const [minutes, setMinutes] = useState<string | undefined>(
    skewedValue ? padTime(skewedValue.getMinutes()) : undefined,
  );

  useEffect((): void => {
    setHours(skewedValue ? padTime(skewedValue.getHours()) : undefined);
    setMinutes(skewedValue ? padTime(skewedValue.getMinutes()) : undefined);
  }, [skewedValue]);

  const handleChangeWithTimezone = () => {
    const _value = skewedValue || new Date();
    const hoursNumber = Number(hours);
    const minutesNumber = Number(minutes);

    if (!isNaN(hoursNumber) && !isNaN(minutesNumber)) {
      const newDate = moment(_value)
        .hours(hoursNumber)
        .minutes(minutesNumber)
        .seconds(0)
        .milliseconds(0)
        .tz(normalizeTimezone(timeZone), true)
        .toDate();

      onChange?.(newDate);
    }
  };

  const onInputChange = (
    inputType: 'hours' | 'minutes',
    newValue: string,
  ): void => {
    const valueNumber = Number(newValue);

    if (!isNaN(valueNumber)) {
      if (inputType === 'hours') {
        if (newValue === '') return setHours('');
        const newHours = Math.max(0, Math.min(23, valueNumber));
        setHours(String(newHours));
      } else if (inputType === 'minutes') {
        if (newValue === '') return setMinutes('');
        const newMinutes = Math.max(0, Math.min(59, valueNumber));
        setMinutes(String(newMinutes));
      }
    }
  };

  const onInputBlur = (event: FocusEvent<HTMLInputElement>): void => {
    // Synchronously flush so the new value reaches the parent before the
    // click that caused the blur (e.g. the date picker OK button) runs.
    // relatedTarget is the element receiving focus and lets us still skip
    // the flush when focus moves between the hh and mm inputs.
    const next = event.relatedTarget;
    if (next !== hourInputRef.current && next !== minuteInputRef.current) {
      handleChangeWithTimezone();
    }
  };

  const onInputKeyPress = (
    inputType: 'hours' | 'minutes',
    event: KeyboardEvent<HTMLInputElement>,
  ): void => {
    const hoursNumber = Number(hours);
    const minutesNumber = Number(minutes);

    switch (event.key) {
      case 'Enter': {
        handleChangeWithTimezone();
        break;
      }

      case 'Escape': {
        event.stopPropagation();
        break;
      }

      case 'ArrowUp': {
        if (inputType === 'hours' && !isNaN(hoursNumber)) {
          setHours(String(Math.min(23, hoursNumber + 1)));
        } else if (inputType === 'minutes' && !isNaN(minutesNumber)) {
          const newMinutes =
            (Math.trunc((minutesNumber + 15 + 7.5) / 15) * 15) % 60;
          const newHours = Math.min(
            23,
            hoursNumber + (minutesNumber > 44 ? 1 : 0),
          );

          setMinutes(newMinutes !== 0 ? newMinutes.toString() : '00');
          setHours(newHours.toString());
        }

        break;
      }

      case 'ArrowDown': {
        if (inputType === 'hours' && !isNaN(hoursNumber)) {
          setHours(String(Math.max(0, hoursNumber - 1)));
        } else if (inputType === 'minutes' && !isNaN(minutesNumber)) {
          const m = minutesNumber - 15;
          const newMinutes =
            m >= 0
              ? (Math.trunc(m / 15) * 15) % 60
              : 60 + Math.trunc(m / 15) * 15;
          const newHours = Math.max(
            0,
            hoursNumber - (newMinutes === 45 ? 1 : 0),
          );

          setMinutes(newMinutes !== 0 ? newMinutes.toString() : '00');
          setHours(newHours.toString());
        }

        break;
      }
    }

    onKeyPress?.(event);
  };

  return (
    <Box
      display="flex"
      alignItems="center"
      width={100}
      height={32}
      color="input.text"
      fontSize={2}
      borderWidth={1}
      borderColor="input.border"
      borderStyle="solid"
      title={`Time in time zone ${timeZone}`}
      bg={disabled ? 'neutral.white_100' : 'neutral.white_50'}
    >
      <Box
        as="input"
        value={hours || ''}
        ref={hourInputRef}
        disabled={disabled}
        maxLength={2}
        onChange={(event: ChangeEvent<HTMLInputElement>) =>
          onInputChange('hours', event.target.value)
        }
        onBlur={onInputBlur}
        onKeyDown={(event: KeyboardEvent<HTMLInputElement>) =>
          onInputKeyPress('hours', event)
        }
        height={30}
        border="none"
        textAlign="right"
        py={2}
        pr={2}
        pl={3}
        placeholder="hh"
        bg="transparent"
      />
      <Box
        width={4}
        flexShrink={0}
        height={30}
        display="flex"
        alignItems="center"
        textAlign="center"
        bg="transparent"
      >
        :
      </Box>
      <Box
        as="input"
        value={minutes || ''}
        ref={minuteInputRef}
        disabled={disabled}
        maxLength={2}
        onChange={(event: ChangeEvent<HTMLInputElement>) =>
          onInputChange('minutes', event.target.value)
        }
        onBlur={onInputBlur}
        onKeyDown={(event: KeyboardEvent<HTMLInputElement>) =>
          onInputKeyPress('minutes', event)
        }
        height={30}
        border="none"
        textAlign="left"
        py={2}
        pl={2}
        pr={3}
        placeholder="mm"
        bg="transparent"
      />
    </Box>
  );
};

export default TimeInput;
